import { useMemo, useState } from 'react';
import { formatNumber } from 'utils/number';
import { useStore } from '@tanstack/react-store';
import { SRPComboBox } from 'components/srp-combobox';
import {
  VolumeSourcePicker,
  type VolumeSourcePickerData,
  type VolumeSourceSelection,
} from 'components/volume-source-picker';
import { DatePicker } from 'components/date-picker';
import { getDateString, parseTypedDate } from 'utils/dates';
import type { AvailableAfe } from 'types/api-responses';
import type {
  BulkEditAfeOptions,
  BulkEditFormInstance,
  BulkEditFormState,
  SelectedSlotSummary,
} from './types';
import { AFE_PICKER_INPUT_ID } from './utils';
import style from './edit-fields-view.module.scss';

/**
 * The AFE picker's mode + inputs. `enumerated` = the classic intersected candidate list,
 * client-filtered in this view and bound to the panel form's `afeSourceId`. `all` = the
 * select-all catalog picker (server-searched + infinite scroll), fully controlled by the
 * caller (slot-view owns the selection + search). Both render the shared `SRPComboBox`.
 */
export type EditFieldsAfe =
  | { mode: 'enumerated'; options: BulkEditAfeOptions }
  | {
      mode: 'all';
      items: AvailableAfe[];
      status: BulkEditAfeOptions['status'];
      errorMessage?: string;
      selectedAfe: AvailableAfe | null;
      onSelect: (afe: AvailableAfe | null) => void;
      onSearchChange: (text: string) => void;
      onLoadMore: () => void;
      hasNextPage: boolean;
      isFetchingNextPage: boolean;
    };

interface EditFieldsViewProps {
  form: BulkEditFormInstance;
  selectedSlots: SelectedSlotSummary[];
  /**
   * All slots in the underlying table — selected or not. Used to populate the AFE option
   * labels with every slot id assigned to each AFE. Defaults to `selectedSlots`.
   */
  allSlots?: SelectedSlotSummary[];
  /** The AFE picker mode + inputs (enumerated client-filter vs. all catalog search). */
  afe: EditFieldsAfe;
  /**
   * When present, renders an "Applying to all N slots" summary in place of the per-slot
   * "Selected slots (N): …" line — for select-all mode, where the ids aren't materialized.
   */
  allSummary?: { total: number; excludedCount: number };
  /** Volume-source picker inputs (INS-11129). Omit to hide the section. */
  volumeSource?: VolumeSourcePickerData;
}

const DATE_FIELDS: Array<{
  key: keyof BulkEditFormState;
  labelText: string;
}> = [
  { key: 'contingenciesCostsDate', labelText: 'Contingency Cost Date' },
  { key: 'facilitiesCostsDate', labelText: 'Facility Cost Date' },
  { key: 'drillingCostsDate', labelText: 'Drilling Cost Date' },
  { key: 'completionCostsDate', labelText: 'Completion Cost Date' },
  { key: 'spudDate', labelText: 'Spud Date' },
  { key: 'permitDate', labelText: 'Permit Date' },
  { key: 'firstProductionDate', labelText: 'First Production Date' },
  { key: 'otherCostsDate', labelText: 'Other Cost Date' },
];

export const EditFieldsView = ({
  form,
  selectedSlots,
  allSlots,
  afe,
  allSummary,
  volumeSource,
}: EditFieldsViewProps) => {
  // Source for the AFE → slot ids label map: every slot in the table (if the panel was
  // given `allRows`), otherwise just the selected ones.
  const labelSourceSlots = allSlots ?? selectedSlots;

  // The AFE option labels list ALL slot ids assigned to each AFE — drawn from
  // `labelSourceSlots` — so the user sees every slot an AFE covers, not only the selected.
  const slotIdsByAfeKey = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const slot of labelSourceSlots) {
      if (slot.afeSourceId === undefined || slot.afeSourceId === '') continue;
      const key = String(slot.afeSourceId);
      const list = map.get(key) ?? [];
      list.push(String(slot.slotId));
      map.set(key, list);
    }
    return map;
  }, [labelSourceSlots]);

  const allSelectedSlotIds = useMemo(
    () => selectedSlots.map((s) => String(s.slotId)),
    [selectedSlots]
  );

  // ── Volume source picker (INS-11129) ──────────────────────────────────────────
  // Narrowing only shortens the forecast list; whatever is chosen applies to the whole
  // selection, like the AFE override.
  const volumeSourceId = useStore(form.store, (s) => s.values.volumeSourceId);

  // The picker reports the five fields as a SET (or null when an upstream change clears
  // it), so a half-changed selection cannot reach the wire.
  const setVolumeSource = (selection: VolumeSourceSelection | null) => {
    form.setFieldValue('volumeSourceId', selection?.volumeSourceId);
    form.setFieldValue('volumeSourceType', selection?.volumeSourceType);
    form.setFieldValue('volumeSourceHref', selection?.volumeSourceHref);
    form.setFieldValue('forecastSource', selection?.forecastSource);
    form.setFieldValue('dcaScenario', selection?.dcaScenario);
  };

  const afeToLabel = (item: AvailableAfe | null): string => {
    if (!item) return '';
    const id = String(item.id);
    const wellName = item.well?.name ?? '—';
    const slotIds = slotIdsByAfeKey.get(id) ?? [];
    const base = `${id} · ${wellName}`;
    return slotIds.length > 0 ? `${base} · ${slotIds.join(', ')}` : base;
  };

  // --- Enumerated AFE state (hooks must run unconditionally; used only in enum mode) ---
  const [enumSearch, setEnumSearch] = useState('');
  const enumAfeSourceId = useStore(form.store, (s) => s.values.afeSourceId);
  const enumOptions = afe.mode === 'enumerated' ? afe.options : null;

  // Client-filter the intersected list by AFE id / well name / slot-id (the filter the
  // Carbon ComboBox did internally, moved here so the shared SRPComboBox stays dumb).
  const enumFilteredItems = useMemo(() => {
    const items = enumOptions?.items ?? [];
    const q = enumSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const id = String(item.id).toLowerCase();
      const wellName = String(item.well?.name ?? '').toLowerCase();
      const slotIds = slotIdsByAfeKey.get(String(item.id)) ?? [];
      return (
        id.includes(q) ||
        wellName.includes(q) ||
        slotIds.some((s) => s.toLowerCase().includes(q))
      );
    });
  }, [enumOptions, enumSearch, slotIdsByAfeKey]);

  // Resolve the selection from the FULL list (not the filtered view) so it still shows
  // when the user types a filter that narrows it out.
  const enumSelectedAfe = useMemo(() => {
    if (!enumOptions || enumAfeSourceId === undefined) return null;
    return (
      enumOptions.items.find((a) => String(a.id) === String(enumAfeSourceId)) ??
      null
    );
  }, [enumOptions, enumAfeSourceId]);

  return (
    <div className={style.body}>
      <h3 className={style.sectionTitle}>Set Values</h3>
      <p className={style.description}>
        Below are all the attributes you can bulk edit across the selected
        slots. Fill in only the fields you want to change — any field left blank
        will be left as-is. The values you set will be applied to all selected
        slots.
      </p>

      {allSummary ? (
        <p className={style.selectedSlots}>
          <strong>
            Applying to all {formatNumber(allSummary.total, 0)} slot
            {allSummary.total === 1 ? '' : 's'}
            {allSummary.excludedCount > 0
              ? ` (${formatNumber(allSummary.excludedCount, 0)} excluded)`
              : ''}
            .
          </strong>
        </p>
      ) : (
        <p className={style.selectedSlots}>
          <strong>Selected slots ({allSelectedSlotIds.length}):</strong>{' '}
          {allSelectedSlotIds.length > 0 ? allSelectedSlotIds.join(', ') : '—'}
        </p>
      )}

      <div className={style.field}>
        {afe.mode === 'all' ? (
          <SRPComboBox<AvailableAfe>
            id={AFE_PICKER_INPUT_ID}
            titleText="AFE"
            placeholder="Search the AFE catalog by AFE source or well name"
            items={afe.items}
            itemToString={afeToLabel}
            selectedItem={afe.selectedAfe}
            onSelect={afe.onSelect}
            onSearchChange={afe.onSearchChange}
            status={afe.status}
            errorMessage={afe.errorMessage}
            errorTitle="Could not load AFEs"
            loadingText="Loading AFEs..."
            emptyText="No AFEs found"
            onLoadMore={afe.onLoadMore}
            hasNextPage={afe.hasNextPage}
            isFetchingNextPage={afe.isFetchingNextPage}
          />
        ) : (
          <SRPComboBox<AvailableAfe>
            id={AFE_PICKER_INPUT_ID}
            titleText="AFE"
            placeholder="Search by AFE source, well name, or slot ID"
            items={enumFilteredItems}
            itemToString={afeToLabel}
            selectedItem={enumSelectedAfe}
            onSelect={(item) =>
              form.setFieldValue('afeSourceId', item ? item.id : undefined)
            }
            onSearchChange={setEnumSearch}
            // The candidate list is static (client-filtered) — disable until it's loaded,
            // preserving the enumerated field's legacy behavior.
            disabled={afe.options.status !== 'ready'}
            status={afe.options.status}
            errorMessage={afe.options.errorMessage}
            errorTitle="Could not load AFEs"
            loadingText="Loading AFEs..."
            emptyText={
              selectedSlots.length === 0
                ? 'No selected slots have a slot ID'
                : 'No AFEs valid for all selected slots'
            }
          />
        )}
      </div>

      {volumeSource && (
        <>
          <h3 className={style.sectionTitle}>Volume Source</h3>

          <VolumeSourcePicker
            idPrefix="bulk-edit-volume"
            data={volumeSource}
            selectedId={volumeSourceId ?? null}
            onSelect={setVolumeSource}
          />
        </>
      )}

      {DATE_FIELDS.map(({ key, labelText }) => (
        <div className={style.field} key={key}>
          <form.Field name={key}>
            {(field) => (
              <DatePicker
                id={`bulk-edit-${key}`}
                labelText={labelText}
                value={field.state.value}
                onChange={(dates) => {
                  field.handleChange(
                    dates[0] ? getDateString(dates[0]) : undefined
                  );
                }}
                // Commits a date the user TYPED. Without this flatpickr discards it on
                // blur — see `parseTypedDate`, which owns the parsing and its reasoning.
                onInputChange={(event) => {
                  field.handleChange(parseTypedDate(event.target.value));
                }}
                appendTo={document.body}
              />
            )}
          </form.Field>
        </div>
      ))}
    </div>
  );
};
